/* Shared homepage commerce: Shopify Ajax + Refresh's native section/pubsub contract. */
(() => {
  'use strict';
  if (window.ZivaBuyCommerce) return;

  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const cartUrl = `${root}cart`;
  const storageKey = 'zivabuy:wishlist:v1';
  const pendingButtons = new WeakSet();
  let mutationQueue = Promise.resolve();
  let savedProducts = [];
  let dialog;
  let dialogTrigger;

  function safeProductURL(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin && /\/products\//.test(url.pathname) ? `${url.pathname}${url.search}` : null;
    } catch { return null; }
  }

  function safeImageURL(value) {
    if (typeof value !== 'string' || !value) return '';
    try {
      const url = new URL(value.startsWith('//') ? `https:${value}` : value, window.location.origin);
      if (url.origin === window.location.origin && url.pathname.startsWith('/cdn/')) return `${url.pathname}${url.search}`;
      return url.protocol === 'https:' ? url.href : '';
    } catch { return ''; }
  }

  async function requestJSON(url, options) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    let data;
    try { data = await response.json(); } catch { throw new Error('The store could not confirm this request. Please try again.'); }
    if (!response.ok || data?.status >= 400) {
      const message = typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : 'Unable to add this item. Please try again.';
      throw new Error(message);
    }
    return data;
  }

  function feedback(button, text, failed = false) {
    const card = button?.closest('[data-zb-commerce-card]') || button?.parentElement;
    if (!card) return;
    const error = card.querySelector('[data-zb-error]');
    const status = card.querySelector('[data-zb-status]');
    if (error) { error.hidden = !failed || !text; error.textContent = failed ? text : ''; }
    if (status) { status.hidden = failed || !text; status.textContent = failed ? '' : text; }
  }

  function updateCounts(cart) {
    document.querySelectorAll('[data-cart-count], [data-zb-cart-count]').forEach((badge) => {
      badge.textContent = String(cart.item_count);
      badge.hidden = cart.item_count === 0;
      badge.style.display = cart.item_count > 0 ? '' : 'none';
    });
  }

  async function refresh(addedState, { openCart = false, trigger } = {}) {
    const cart = await requestJSON(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      const sectionIds = drawer.getSectionsToRender().map((section) => section.id);
      let sections = addedState?.sections;
      const validSections = () => sectionIds.every((id) => typeof sections?.[id] === 'string' && sections[id].trim());
      if (!validSections()) {
        sections = await requestJSON(`${cartUrl}?sections=${encodeURIComponent(sectionIds.join(','))}`, { headers: { Accept: 'application/json' } });
      }
      if (validSections()) {
        if (trigger?.isConnected && drawer.setActiveElement) drawer.setActiveElement(trigger);
        drawer.classList.toggle('is-empty', cart.item_count === 0);
        if (openCart) {
          drawer.renderContents({ ...addedState, sections });
        } else {
          // renderContents always opens Refresh's drawer, so refresh fragments directly for Buy Now.
          drawer.getSectionsToRender().forEach((section) => {
            const source = new DOMParser().parseFromString(sections[section.id], 'text/html').querySelector(section.selector || '.shopify-section');
            const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            if (source && target) target.replaceChildren(...source.childNodes);
          });
        }
      } else if (openCart) {
        // A confirmed addition must never leave a stale empty drawer visible.
        window.location.assign(cartUrl);
      }
    } else if (openCart) {
      window.location.assign(cartUrl);
    }
    updateCounts(cart);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try { await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zivabuy-commerce', cartData: cart }); } catch (error) { console.warn('[ZivaBuy] A cart subscriber could not refresh.', error); }
    }
    document.dispatchEvent(new CustomEvent('zivabuy:cart-updated', { detail: { cart } }));
    return cart;
  }

  async function performAddition({ variantId, quantity = 1, button, checkout = false, openCart = true }) {
    const id = String(variantId || '');
    const count = Number(quantity);
    if (!/^\d+$/.test(id) || !Number.isInteger(count) || count < 1) throw new Error('Please choose an available product and quantity.');
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || ['cart-icon-bubble'];
    const payload = { items: [{ id, quantity: count }], sections, sections_url: window.location.pathname };
    const added = await requestJSON(`${root}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(payload),
    });
    if (!added?.items?.length && !added?.id) throw new Error('The store did not confirm an added item. Please try again.');
    try { await refresh(added, { openCart: openCart && !checkout, trigger: button }); } catch {
      // The add itself succeeded. Route to the authoritative cart if rendering failed;
      // never encourage a duplicate retry or claim that the confirmed add failed.
      window.location.assign(checkout ? `${root}checkout` : cartUrl);
      return added;
    }
    if (checkout) window.location.assign(`${root}checkout`);
    return added;
  }

  async function add(options) {
    const button = options.button;
    if (button && (pendingButtons.has(button) || button.disabled)) return;
    const originalNodes = button ? [...button.childNodes].map((node) => node.cloneNode(true)) : [];
    if (button) {
      pendingButtons.add(button);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = options.checkout ? 'Preparing checkout…' : 'Adding…';
      feedback(button, '');
    }
    const task = mutationQueue.catch(() => {}).then(() => performAddition(options));
    mutationQueue = task;
    try {
      const result = await task;
      feedback(button, options.checkout ? 'Opening checkout…' : 'Added to your cart.');
      return result;
    } catch (error) {
      feedback(button, error.message || 'Unable to add this item. Please try again.', true);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try { await publish(PUB_SUB_EVENTS.cartError, { source: 'zivabuy-commerce', productVariantId: String(options.variantId || ''), message: error.message }); } catch { /* Keep the purchase error visible if a subscriber fails. */ }
      }
      throw error;
    } finally {
      if (button) {
        button.replaceChildren(...originalNodes);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        pendingButtons.delete(button);
      }
    }
  }

  function readSavedProducts() {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      savedProducts = Array.isArray(value) ? value.filter((item) => item && /^\d+$/.test(String(item.id)) && safeProductURL(item.url)).map((item) => ({ id: String(item.id), title: String(item.title || 'Saved product').replace(/[🌿🏔🍬\uFE0F]/gu, '').trim(), url: safeProductURL(item.url), image: safeImageURL(item.image) })) : [];
      // Preserve older lower-section saves when those products are present on this page.
      const legacy = JSON.parse(localStorage.getItem('wishlist_items') || '[]');
      if (Array.isArray(legacy)) document.querySelectorAll('[data-zb-wishlist]').forEach((button) => {
        if (legacy.map(String).includes(button.dataset.productId) && !savedProducts.some((item) => item.id === button.dataset.productId)) {
          const product = productFromButton(button);
          if (product) savedProducts.push(product);
        }
      });
    } catch { /* Storage may be unavailable; saved items remain usable for this visit. */ }
  }

  function productFromButton(button) {
    const url = safeProductURL(button.dataset.productUrl);
    const id = String(button.dataset.productId || '');
    return url && /^\d+$/.test(id) ? { id, title: button.dataset.productTitle || 'Saved product', url, image: safeImageURL(button.dataset.productImage) } : null;
  }

  function syncSavedControls() {
    document.querySelectorAll('[data-zb-wishlist]').forEach((button) => {
      const saved = savedProducts.some((item) => item.id === String(button.dataset.productId));
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${button.dataset.productTitle || 'product'}${saved ? ' from saved items' : ''}`);
      button.classList.toggle('active', saved);
    });
    document.querySelectorAll('[data-zb-saved-count]').forEach((count) => { count.textContent = String(savedProducts.length); count.hidden = savedProducts.length === 0; });
    document.querySelectorAll('[data-zb-saved-open]').forEach((button) => { button.setAttribute('aria-label', `View saved items (${savedProducts.length})`); });
  }

  function persistSavedProducts() {
    try {
      localStorage.setItem(storageKey, JSON.stringify(savedProducts));
      localStorage.removeItem('wishlist_items');
    } catch { /* A privacy-restricted browser can still use a session-only saved list. */ }
    syncSavedControls();
    if (dialog?.open) renderSavedList();
  }

  function toggleSaved(button) {
    const product = productFromButton(button);
    if (!product) return;
    const index = savedProducts.findIndex((item) => item.id === product.id);
    if (index < 0) savedProducts.push(product); else savedProducts.splice(index, 1);
    persistSavedProducts();
    document.dispatchEvent(new CustomEvent('zivabuy:wishlist-updated', { detail: { count: savedProducts.length } }));
  }

  function renderSavedList() {
    const list = dialog.querySelector('[data-zb-saved-list]');
    list.replaceChildren();
    if (!savedProducts.length) {
      const empty = document.createElement('p');
      empty.textContent = 'Your saved list is empty. Use a product’s heart to keep it here.';
      list.append(empty);
      return;
    }
    savedProducts.forEach((product) => {
      const row = document.createElement('div'); row.className = 'zb-saved-item';
      const link = document.createElement('a'); link.href = product.url; link.className = 'zb-saved-product';
      if (product.image) { const image = document.createElement('img'); image.src = product.image; image.alt = ''; image.width = 72; image.height = 72; image.loading = 'lazy'; link.append(image); }
      const title = document.createElement('span'); title.textContent = product.title; link.append(title);
      const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'zb-saved-remove'; remove.textContent = 'Remove'; remove.setAttribute('aria-label', `Remove ${product.title} from saved items`);
      remove.addEventListener('click', () => { savedProducts = savedProducts.filter((item) => item.id !== product.id); persistSavedProducts(); dialog.querySelector('h2').focus(); });
      row.append(link, remove); list.append(row);
    });
  }

  function openSaved(trigger) {
    if (!dialog) {
      dialog = document.createElement('dialog'); dialog.className = 'zb-saved-dialog'; dialog.setAttribute('aria-labelledby', 'zb-saved-title');
      const header = document.createElement('div'); header.className = 'zb-saved-header';
      const heading = document.createElement('h2'); heading.id = 'zb-saved-title'; heading.textContent = 'Saved items'; heading.tabIndex = -1;
      const close = document.createElement('button'); close.type = 'button'; close.className = 'zb-saved-close'; close.textContent = 'Close'; close.addEventListener('click', () => dialog.close());
      const list = document.createElement('div'); list.setAttribute('data-zb-saved-list', ''); list.className = 'zb-saved-list';
      header.append(heading, close); dialog.append(header, list); document.body.append(dialog);
      dialog.addEventListener('close', () => dialogTrigger?.focus());
      dialog.addEventListener('click', (event) => { if (event.target === dialog) { const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); } });
    }
    dialogTrigger = trigger; renderSavedList(); dialog.showModal(); dialog.querySelector('h2').focus();
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-zb-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = event.submitter || form.querySelector('[data-zb-add]');
    if (!button) return;
    add({ variantId: button.dataset.variantId, quantity: Number(form.elements.quantity?.value || 1), button, checkout: button.dataset.checkout === 'true' }).catch(() => {});
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-zb-add], [data-zb-wishlist], [data-zb-saved-open]');
    if (!button || button.disabled) return;
    if (button.hasAttribute('data-zb-wishlist')) { event.preventDefault(); toggleSaved(button); }
    else if (button.hasAttribute('data-zb-saved-open')) { event.preventDefault(); openSaved(button); }
    else if (!button.closest('[data-zb-product-form]')) {
      event.preventDefault();
      add({ variantId: button.dataset.variantId, quantity: Number(button.dataset.quantity || 1), button, checkout: button.dataset.checkout === 'true' }).catch(() => {});
    }
  });

  window.addEventListener('storage', (event) => { if (event.key === storageKey || event.key === null) { readSavedProducts(); syncSavedControls(); if (dialog?.open) renderSavedList(); } });
  document.addEventListener('shopify:section:load', () => { syncSavedControls(); });
  readSavedProducts(); syncSavedControls();
  window.ZivaBuyCommerce = { add, refresh, openSaved, syncSavedControls };
})();
